import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  pdf,
} from '@react-pdf/renderer';
import {
  OrderReportResponse,
  OrderReportItem,
} from 'src/models/Management.interface';
import { getCompanyInfoForPDF, CompanyInfoData } from 'src/utils/company.utils';
import { formatDate, formatCurrency } from 'src/utils/pdf.utils';
import Logo from 'src/assets/images/Logo.png';
import dayjs from 'dayjs';

const colors = {
  primary: '#1a3c5e',
  secondary: '#3a7bd5',
  textDark: '#1a1a2e',
  textMedium: '#555555',
  textLight: '#888888',
  border: '#e0e0e0',
  borderLight: '#f0f0f0',
  backgroundGray: '#f7f8fa',
  white: '#ffffff',
  success: '#27ae60',
  error: '#c0392b',
};

const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontSize: 10,
    fontFamily: 'Helvetica',
    backgroundColor: colors.white,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 20,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    paddingBottom: 20,
  },
  companyInfo: {
    flexDirection: 'column',
  },
  companyName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: colors.primary,
    marginBottom: 4,
  },
  companyDetails: {
    fontSize: 9,
    color: colors.textMedium,
    marginBottom: 2,
  },
  reportTitleContainer: {
    alignItems: 'flex-end',
  },
  reportTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: colors.primary,
    marginBottom: 4,
  },
  reportDate: {
    fontSize: 10,
    color: colors.textMedium,
  },
  filterSection: {
    marginBottom: 20,
    padding: 12,
    backgroundColor: colors.backgroundGray,
    borderRadius: 8,
  },
  filterTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    color: colors.primary,
    marginBottom: 8,
    textTransform: 'uppercase',
  },
  filterGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  filterItem: {
    width: '33%',
    marginBottom: 4,
  },
  filterLabel: {
    fontSize: 8,
    color: colors.textLight,
  },
  filterValue: {
    fontSize: 9,
    fontWeight: 'bold',
    color: colors.textDark,
  },
  summarySection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 24,
  },
  statCard: {
    width: '23%',
    padding: 10,
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.borderLight,
    borderRadius: 8,
    alignItems: 'flex-end',
  },
  statLabel: {
    fontSize: 7,
    color: colors.textLight,
    marginBottom: 4,
    textTransform: 'uppercase',
  },
  statValue: {
    fontSize: 11,
    fontWeight: 'bold',
    color: colors.textDark,
  },
  table: {
    width: '100%',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: colors.primary,
    padding: 8,
  },
  tableHeaderCell: {
    color: colors.white,
    fontSize: 8,
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: colors.borderLight,
    padding: 8,
  },
  tableCell: {
    fontSize: 8,
    color: colors.textDark,
  },
  cellOrderNo: { width: '15%' },
  cellDate: { width: '20%' },
  cellCustomer: { width: '25%' },
  cellStatus: { width: '10%', textAlign: 'center' },
  cellAmount: { width: '10%', textAlign: 'right' },
  cellPaid: { width: '10%', textAlign: 'right' },
  cellOutstanding: { width: '10%', textAlign: 'right' },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 40,
    right: 40,
    borderTopWidth: 1,
    borderTopColor: colors.border,
    paddingTop: 10,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  footerText: {
    fontSize: 8,
    color: colors.textLight,
  },
  poweredBy: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  logo: {
    width: 40,
    height: 12,
    marginLeft: 4,
  },
  productsSoldSection: {
    marginTop: 20,
  },
  productsSoldTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    marginBottom: 10,
    color: colors.primary,
  },
  cellProductName: { width: '50%' },
  cellQuantitySold: { width: '25%', textAlign: 'right' },
  cellTotalAmount: { width: '25%', textAlign: 'right' },
});

interface OrderReportPDFProps {
  data: OrderReportResponse;
  filters: {
    startDate: string | null;
    endDate: string | null;
    statuses: string[];
    customerName?: string | null;
  };
  companyInfo?: CompanyInfoData;
}

const OrderReportDocument: React.FC<OrderReportPDFProps> = ({
  data,
  filters,
  companyInfo,
}) => {
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <View style={styles.companyInfo}>
            <Text style={styles.companyName}>
              {companyInfo?.companyName || 'OrderX Management'}
            </Text>
            <Text style={styles.companyDetails}>
              {companyInfo?.addressLine1 || ''}
            </Text>
            <Text style={styles.companyDetails}>
              {companyInfo?.city ? `${companyInfo.city}, ` : ''}
              {companyInfo?.postCode || ''}
            </Text>
            <Text style={styles.companyDetails}>
              {companyInfo?.email || ''}
            </Text>
          </View>
          <View style={styles.reportTitleContainer}>
            <Text style={styles.reportTitle}>Order Report</Text>
            <Text style={styles.reportDate}>
              Generated: {dayjs().format('DD MMM YYYY, HH:mm')}
            </Text>
          </View>
        </View>

        <View style={styles.filterSection}>
          <Text style={styles.filterTitle}>Report Parameters</Text>
          <View style={styles.filterGrid}>
            <View style={styles.filterItem}>
              <Text style={styles.filterLabel}>Customer</Text>
              <Text style={styles.filterValue}>
                {filters.customerName || 'All Customers'}
              </Text>
            </View>
            <View style={styles.filterItem}>
              <Text style={styles.filterLabel}>Date Range</Text>
              <Text style={styles.filterValue}>
                {filters.startDate
                  ? dayjs(filters.startDate).format('DD/MM/YYYY')
                  : 'All'}{' '}
                -{' '}
                {filters.endDate
                  ? dayjs(filters.endDate).format('DD/MM/YYYY')
                  : 'All'}
              </Text>
            </View>
            <View style={styles.filterItem}>
              <Text style={styles.filterLabel}>Statuses</Text>
              <Text style={styles.filterValue}>
                {filters.statuses.length > 5
                  ? 'Multiple Selected'
                  : filters.statuses.join(', ')}
              </Text>
            </View>
          </View>
        </View>

        <View style={styles.summarySection}>
          <View style={styles.statCard}>
            <Text style={styles.statLabel}>Total Orders</Text>
            <Text style={styles.statValue}>{data.summary.totalOrders}</Text>
          </View>
          <View style={styles.statCard}>
            <Text style={styles.statLabel}>Total Revenue</Text>
            <Text style={styles.statValue}>
              {formatCurrency(data.summary.totalRevenue)}
            </Text>
          </View>
          <View style={styles.statCard}>
            <Text style={styles.statLabel}>Total Received</Text>
            <Text style={[styles.statValue, { color: colors.secondary }]}>
              {formatCurrency(data.summary.totalPaid)}
            </Text>
          </View>
          <View style={styles.statCard}>
            <Text style={styles.statLabel}>Outstanding</Text>
            <Text style={[styles.statValue, { color: colors.error }]}>
              {formatCurrency(data.summary.totalOutstanding)}
            </Text>
          </View>
        </View>

        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={[styles.tableHeaderCell, styles.cellOrderNo]}>
              Order #
            </Text>
            <Text style={[styles.tableHeaderCell, styles.cellDate]}>
              Date & Time
            </Text>
            <Text style={[styles.tableHeaderCell, styles.cellCustomer]}>
              Customer
            </Text>
            <Text style={[styles.tableHeaderCell, styles.cellStatus]}>
              Status
            </Text>
            <Text style={[styles.tableHeaderCell, styles.cellAmount]}>
              Amount
            </Text>
            <Text style={[styles.tableHeaderCell, styles.cellPaid]}>
              Received
            </Text>
            <Text style={[styles.tableHeaderCell, styles.cellOutstanding]}>
              Bal.
            </Text>
          </View>

          {data.orders.map((order: OrderReportItem, index: number) => (
            <View
              key={order.id}
              style={[
                styles.tableRow,
                { backgroundColor: index % 2 === 0 ? '#fff' : '#fcfcfc' },
              ]}
            >
              <Text style={[styles.tableCell, styles.cellOrderNo]}>
                {order.orderNumber}
              </Text>
              <Text style={[styles.tableCell, styles.cellDate]}>
                {dayjs(order.createdAt).format('DD/MM/YY HH:mm')}
              </Text>
              <Text style={[styles.tableCell, styles.cellCustomer]}>
                {order.customerName}
              </Text>
              <Text style={[styles.tableCell, styles.cellStatus]}>
                {order.status}
              </Text>
              <Text style={[styles.tableCell, styles.cellAmount]}>
                {formatCurrency(order.grandTotal)}
              </Text>
              <Text style={[styles.tableCell, styles.cellPaid]}>
                {formatCurrency(order.paidAmount)}
              </Text>
              <Text style={[styles.tableCell, styles.cellOutstanding]}>
                {formatCurrency(order.grandTotal - order.paidAmount)}
              </Text>
            </View>
          ))}
        </View>

        {/* Products Sold Table */}
        {data.productsSold && data.productsSold.length > 0 && (
          <View break style={styles.productsSoldSection}>
            <Text style={styles.productsSoldTitle}>Products Sold Summary</Text>
            <View style={styles.table}>
              <View style={styles.tableHeader}>
                <Text style={[styles.tableHeaderCell, styles.cellProductName]}>
                  Product Name
                </Text>
                <Text style={[styles.tableHeaderCell, styles.cellQuantitySold]}>
                  Quantity Sold
                </Text>
                <Text style={[styles.tableHeaderCell, styles.cellTotalAmount]}>
                  Total Amount
                </Text>
              </View>
              {data.productsSold.map((product, index) => (
                <View
                  key={product.productId}
                  style={[
                    styles.tableRow,
                    { backgroundColor: index % 2 === 0 ? '#fff' : '#fcfcfc' },
                  ]}
                >
                  <Text style={[styles.tableCell, styles.cellProductName]}>
                    {product.productName}
                  </Text>
                  <Text style={[styles.tableCell, styles.cellQuantitySold]}>
                    {product.quantitySold.toLocaleString()}
                  </Text>
                  <Text style={[styles.tableCell, styles.cellTotalAmount]}>
                    {formatCurrency(product.totalRevenue)}
                  </Text>
                </View>
              ))}
            </View>
          </View>
        )}

        <View style={styles.footer} fixed>
          <View style={styles.poweredBy}>
            <Text style={styles.footerText}>POWERED BY</Text>
            {/* If Logo path works, great. Using text as fallback if needed but keeping Image for consistency with other PDFs */}
            <Image style={styles.logo} src={Logo} />
          </View>
        </View>
      </Page>
    </Document>
  );
};

export const generateOrderReportPDF = async (
  data: OrderReportResponse,
  filters: {
    startDate: string | null;
    endDate: string | null;
    statuses: string[];
    customerName?: string | null;
  }
): Promise<void> => {
  try {
    const companyInfo = await getCompanyInfoForPDF();

    const blob = await pdf(
      <OrderReportDocument
        data={data}
        filters={filters}
        companyInfo={companyInfo}
      />
    ).toBlob();

    const fileName = `OrderReport-${dayjs().format('YYYYMMDD-HHmm')}.pdf`;
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 100);
  } catch (error) {
    console.error('Error generating Order Report PDF:', error);
    throw error;
  }
};
